import { BaseApiService } from '@doccareplus/api-client';

/**
 * Scheduling infrastructure — HTTP adapter for the clinic schedule + queue
 * (calendar reads, booking, walk-ins, and the full appointment lifecycle).
 *
 * Model B header-based auth: the clinic id and active role are injected globally
 * by AuthSessionBridge, so no clinic id lives in any URL or body — only the
 * appointment id the client actually knows.
 *
 * All JSON is camelCase; these types mirror the C# Contracts records 1:1.
 */

/** Mirrors Appointments.Common.AppointmentResponse. Times are ISO-8601 UTC. */
export interface AppointmentResponse {
  id: string;
  /** "Staff" | "Portal" | "WalkIn" */
  source: string;
  /** "Requested" | "Confirmed" | "CheckedIn" | "InProgress" | "Completed" | "Cancelled" | "NoShow" | "Rejected" */
  status: string;
  startTime: string;
  durationMinutes: number;
  patientId?: string | null;
  /** Linked patient's full name, resolved server-side for display (null when no patient is linked). */
  patientName?: string | null;
  tempPatientName?: string | null;
  tempPatientPhone?: string | null;
  providerId?: string | null;
  roomId?: string | null;
  serviceId?: string | null;
  reason?: string | null;
  internalNote?: string | null;
  patientNote?: string | null;
  checkedInAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  queueToken?: number | null;
  isSkipped: boolean;
  skipReason?: string | null;
  queueOrder?: number | null;
  cancellationReason?: string | null;
  visitId?: string | null;
  intakeCompleted?: boolean;
  reminderEnabled?: boolean | null;
  createdAt: string;
  updatedAt?: string | null;
  waitMinutes?: number | null;
  estimatedWaitMinutes?: number | null;
  waitConfidence?: string | null;
  operationalPriority?: 'Routine' | 'Priority' | 'Emergency';
  prioritySetAt?: string | null;
  prioritySetBy?: string | null;
}

/** Mirrors QueueCopilot.Common.QueueRecommendationDto. A single active copilot recommendation. */
export interface QueueRecommendationDto {
  id: string;
  /** "AttentionLongWait" | "RecallSkippedPatient" | "AssignProvider" | "ServeNext" | "AttentionStalledVisit" */
  type: string;
  /** "Pending" | "Approved" | "Rejected" | "Expired" | "Superseded" | "Failed" */
  status: string;
  primaryAppointmentId?: string | null;
  suggestedProviderId?: string | null;
  suggestedQueueOrder?: number | null;
  /** Controlled reason codes (localization keys), never free text. */
  reasonCodes: string[];
  /** "Low" | "Medium" | "High" */
  confidence: string;
  createdAt: string;
  expiresAt: string;
}

/** Mirrors QueueCopilot.Common.QueueCopilotBoardResponse. */
export interface QueueCopilotBoardResponse {
  rows: AppointmentResponse[];
  recommendations: QueueRecommendationDto[];
  /** Smart Queue is enabled for the clinic; when false the copilot surface is hidden. */
  assistanceEnabled: boolean;
  /** Fail-open flag: the engine errored, so recommendations are unavailable but the manual board still renders. */
  assistanceUnavailable: boolean;
  generatedAt?: string | null;
}

/** Mirrors QueueCopilot.SetSmartQueueAssistance.SmartQueueAssistanceResponse. */
export interface SmartQueueAssistanceResponse {
  enabled: boolean;
}

/** Mirrors Appointments.GetQueueCompletedCount.QueueCompletedCountResponse. */
export interface QueueCompletedCountResponse {
  total: number;
}

export interface QueueVisitSnapshot {
  id: string;
  version: number;
  vitals?: {
    weight?: number | null;
    weightUnit?: string | null;
    systolic?: number | null;
    diastolic?: number | null;
    temperature?: number | null;
    temperatureUnit?: string | null;
    pulse?: number | null;
  } | null;
}

export interface QueueVitalsBody {
  weight: number | null;
  weightUnit: string;
  systolic: number | null;
  diastolic: number | null;
  temperature: number | null;
  temperatureUnit: string;
  pulse: number | null;
  version: number;
}

export type IntakeFieldKey = 'Weight' | 'BloodPressure' | 'Temperature' | 'Pulse';
export interface IntakeFormResponse {
  clinicType: 'Allopathy' | 'Homeopathy' | 'Ayurveda' | 'Dental' | 'Physiotherapy';
  fields: Array<{ key: IntakeFieldKey; labelKey: string; required: boolean }>;
  weightUnit: 'kg' | 'lb';
  temperatureUnit: 'C' | 'F';
}

/** Query for GET /clinic/appointments (GetAppointmentsRequest). ISO date-times, half-open [from, to). */
export interface GetAppointmentsParams {
  from: string;
  to: string;
  providerId?: string;
}

/** Body for POST /clinic/appointments (BookStaffAppointmentRequest). */
export interface BookStaffAppointmentRequest {
  startTime: string;
  durationMinutes: number;
  patientId?: string | null;
  providerId?: string | null;
  roomId?: string | null;
  serviceId?: string | null;
  reason?: string | null;
  internalNote?: string | null;
  patientNote?: string | null;
}

/** Body for POST /clinic/appointments/walk-in (CreateWalkInRequest). */
export interface CreateWalkInRequest {
  durationMinutes: number;
  startTime?: string | null;
  patientId?: string | null;
  /** Temporary details for a walk-in with no clinic record yet; linked/created at check-in. */
  tempPatientName?: string | null;
  tempPatientPhone?: string | null;
  providerId?: string | null;
  roomId?: string | null;
  serviceId?: string | null;
  reason?: string | null;
}

/** A backward status correction target the domain accepts. */
export type CorrectableStatus = 'Confirmed' | 'CheckedIn' | 'InProgress';

/**
 * Body for inline patient registration during booking. Name + phone are required; the rest are the
 * same optional demographic fields the full patients-directory register form captures, surfaced here
 * for the booking "register patient" step (`POST /clinic/patients` already accepts the full shape).
 */
export interface QuickRegisterPatientRequest {
  fullName: string;
  phone: string;
  dateOfBirth?: string | null;
  gender?: string | null;
  email?: string | null;
  bloodGroup?: string | null;
  address?: string | null;
  notes?: string | null;
  duplicateOverrideReason?: string | null;
}

/** Lean patient row for the booking patient picker (subset of the patients directory list). */
export interface PatientPickOption {
  id: string;
  fullName: string;
  patientNumber: string;
  phone?: string | null;
}
export interface QuickPatientDuplicateMatch extends PatientPickOption {
  dateOfBirth?: string | null;
  email?: string | null;
  status: string;
  matchReasons: string[];
}

/** A bookable provider (a clinic staff member), for the provider picker. */
export interface ProviderOption {
  id: string;
  name: string;
}

export interface StaffBookingSlot {
  startTime: string;
}

export interface StaffBookingOptions {
  allowFreeFormScheduling: boolean;
  timezone: string;
  slots: StaffBookingSlot[];
  unavailableReason: 'ProviderAvailabilityNotConfigured' | 'NoSlotsForDate' | null;
}

/**
 * Advisory overload signal for a candidate (provider, start time): how many non-cancelled appointments
 * that provider already has in the same fixed 30-minute bracket, the clinic's configured threshold
 * (0 = warning disabled), and whether count meets/exceeds it. See .ai/BusinessDomain/scheduling-queue.md,
 * "Appointment Overload" — warn-only, never blocks booking.
 */
export interface AppointmentBracketLoadResponse {
  count: number;
  threshold: number;
  isOverloaded: boolean;
}

export type BookingAdvisoryWarning =
  | 'ProviderOverlap'
  | 'RoomOverlap'
  | 'ProviderOffHours'
  | 'ProviderLeave'
  | 'ClinicClosure';

export interface BookingAdvisoryResponse {
  warnings: BookingAdvisoryWarning[];
}

/** Raw shapes from the shared patient/staff endpoints the pickers reuse (no cross-feature import). */
interface PaginatedPatients {
  items: PatientPickOption[];
}
class SchedulingApiService extends BaseApiService {
  constructor() {
    super('/clinic');
  }

  /** Server-side patient search for the booking picker (reuses the patients directory endpoint). */
  async searchPatients(search: string): Promise<PatientPickOption[]> {
    const page = await this.get<PaginatedPatients>('/patients', {
      params: { search, page: 1, pageSize: 10 },
    });
    return page.items;
  }

  /** Quick-register a patient inline during booking (name + phone only, reuses the patients endpoint). */
  async quickRegisterPatient(body: QuickRegisterPatientRequest): Promise<PatientPickOption> {
    const created = await this.post<{ id: string; fullName: string; patientNumber: string; phone?: string | null }, QuickRegisterPatientRequest>('/patients', body);
    return { id: created.id, fullName: created.fullName, patientNumber: created.patientNumber, phone: created.phone ?? body.phone };
  }

  /** Backend-filtered providers: active membership plus one active role with visit-write and signing. */
  async listProviders(): Promise<ProviderOption[]> {
    return this.get<ProviderOption[]>('/appointments/providers');
  }

  /** Advisory overload check for the booking drawer — see `AppointmentBracketLoadResponse`. */
  getBracketLoad(providerId: string, startTime: string): Promise<AppointmentBracketLoadResponse> {
    return this.get<AppointmentBracketLoadResponse>('/appointments/bracket-load', {
      params: { providerId, startTime },
    });
  }

  list(params: GetAppointmentsParams): Promise<AppointmentResponse[]> {
    return this.get<AppointmentResponse[]>('/appointments', { params });
  }

  getById(appointmentId: string): Promise<AppointmentResponse> {
    return this.get<AppointmentResponse>(`/appointments/${appointmentId}`);
  }

  /**
   * Today's queue board. The active lanes are returned in full, but the Completed lane carries only the most
   * recent page (the server caps it), so the payload stays flat even at 1000+ visits/day. The Completed lane's
   * true total comes from {@link getCompletedCount}.
   */
  getQueue(): Promise<AppointmentResponse[]> {
    return this.get<AppointmentResponse[]>('/queue');
  }

  /** True total of today's Completed queue rows — backs the Completed lane badge while the board is capped. */
  getCompletedCount(): Promise<QueueCompletedCountResponse> {
    return this.get<QueueCompletedCountResponse>('/queue/completed-count');
  }

  /** Manually set a checked-in item's queue position (advance/defer). */
  reorder(appointmentId: string, queueOrder: number): Promise<AppointmentResponse> {
    return this.put<AppointmentResponse, { queueOrder: number }>(
      `/appointments/${appointmentId}/queue-order`,
      { queueOrder },
    );
  }

  getBookingOptions(date: string, providerId: string, durationMinutes: number): Promise<StaffBookingOptions> {
    return this.get<StaffBookingOptions>('/appointments/booking-options', {
      params: { date, providerId: providerId || undefined, durationMinutes },
    });
  }

  getBookingAdvisories(appointmentId: string, proposedStartTime: string): Promise<BookingAdvisoryResponse> {
    return this.get<BookingAdvisoryResponse>(`/appointments/${appointmentId}/booking-advisories`, {
      params: { proposedStartTime },
    });
  }

  swapQueueItems(
    appointmentId: string,
    otherAppointmentId: string,
    expectedQueueOrder: number,
    expectedOtherQueueOrder: number,
  ): Promise<AppointmentResponse[]> {
    return this.put<AppointmentResponse[], {
      otherAppointmentId: string;
      expectedQueueOrder: number;
      expectedOtherQueueOrder: number;
    }>(`/appointments/${appointmentId}/queue-order/swap`, {
      otherAppointmentId,
      expectedQueueOrder,
      expectedOtherQueueOrder,
    });
  }

  /** Authorized backward status correction (e.g. InProgress → CheckedIn). */
  correctStatus(appointmentId: string, targetStatus: CorrectableStatus, reason?: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, { targetStatus: string; reason?: string }>(
      `/appointments/${appointmentId}/correct-status`,
      { targetStatus, reason },
    );
  }

  /** Pending portal requests awaiting clinic confirmation (Requested, any date). */
  getRequests(): Promise<AppointmentResponse[]> {
    return this.get<AppointmentResponse[]>('/appointments/requests');
  }

  book(body: BookStaffAppointmentRequest): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, BookStaffAppointmentRequest>('/appointments', body);
  }

  createWalkIn(body: CreateWalkInRequest): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, CreateWalkInRequest>('/appointments/walk-in', body);
  }

  confirm(appointmentId: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse>(`/appointments/${appointmentId}/confirm`);
  }

  reject(appointmentId: string, reason?: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, { reason?: string }>(`/appointments/${appointmentId}/reject`, { reason });
  }

  cancel(appointmentId: string, reason?: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, { reason?: string }>(`/appointments/${appointmentId}/cancel`, { reason });
  }

  reschedule(appointmentId: string, newStartTime: string, reason?: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, { newStartTime: string; reason?: string }>(
      `/appointments/${appointmentId}/reschedule`,
      { newStartTime, reason }
    );
  }

  checkIn(appointmentId: string, patientId?: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, { patientId?: string }>(`/appointments/${appointmentId}/check-in`, { patientId });
  }

  start(appointmentId: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse>(`/appointments/${appointmentId}/start`);
  }

  /**
   * Ensures a linked draft visit exists for a checked-in appointment so pre-visit quick intake can be recorded
   * before the doctor starts. Idempotent — returns the appointment (with its visitId) whether the draft was just
   * created or already existed. The appointment stays CheckedIn.
   */
  ensureDraftVisit(appointmentId: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse>(`/appointments/${appointmentId}/draft-visit`);
  }

  complete(appointmentId: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse>(`/appointments/${appointmentId}/complete`);
  }

  markNoShow(appointmentId: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse>(`/appointments/${appointmentId}/no-show`);
  }

  skip(appointmentId: string, reason?: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, { reason?: string }>(`/appointments/${appointmentId}/skip`, { reason });
  }

  unskip(appointmentId: string): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse>(`/appointments/${appointmentId}/unskip`);
  }

  assignProvider(appointmentId: string, providerId: string): Promise<AppointmentResponse> {
    return this.put<AppointmentResponse, { providerId: string }>(`/appointments/${appointmentId}/provider`, { providerId });
  }

  checkPatientDuplicates(body: { fullName: string; phone: string }): Promise<QuickPatientDuplicateMatch[]> {
    return this.post<QuickPatientDuplicateMatch[], { fullName: string; phone: string }>('/patients/duplicates/check', body);
  }

  transferInProgressProvider(
    appointmentId: string,
    receivingProviderId: string,
    reason: string,
  ): Promise<AppointmentResponse> {
    return this.post<AppointmentResponse, { receivingProviderId: string; reason: string }>(
      `/appointments/${appointmentId}/provider-handoff`,
      { receivingProviderId, reason },
    );
  }

  setOperationalPriority(
    appointmentId: string,
    priority: 'Routine' | 'Priority' | 'Emergency',
  ): Promise<AppointmentResponse> {
    return this.put<AppointmentResponse, { priority: string }>(
      `/appointments/${appointmentId}/operational-priority`,
      { priority },
    );
  }

  // ---- Smart Queue copilot ----

  /** Queue board enriched with wait estimates plus the active recommendations and assistance status flags. */
  getCopilotBoard(): Promise<QueueCopilotBoardResponse> {
    return this.get<QueueCopilotBoardResponse>('/queue/copilot');
  }

  /** (Re)generate the clinic's recommendations from the current queue snapshot. */
  refreshRecommendations(): Promise<QueueRecommendationDto[]> {
    return this.post<QueueRecommendationDto[]>('/queue/copilot/recommendations/refresh');
  }

  /** Approve a recommendation — applies the underlying queue action (unskip/assign/reorder). */
  approveRecommendation(recommendationId: string): Promise<QueueRecommendationDto> {
    return this.post<QueueRecommendationDto>(`/queue/copilot/recommendations/${recommendationId}/approve`);
  }

  /** Dismiss a recommendation without applying it. */
  rejectRecommendation(recommendationId: string): Promise<QueueRecommendationDto> {
    return this.post<QueueRecommendationDto>(`/queue/copilot/recommendations/${recommendationId}/reject`);
  }

  /** Toggle clinic-wide Smart Queue assistance (SmartQueueEnabled). Requires clinic.settings.manage. */
  setSmartQueueAssistance(enabled: boolean): Promise<SmartQueueAssistanceResponse> {
    return this.put<SmartQueueAssistanceResponse, { enabled: boolean }>('/queue/copilot/assistance', { enabled });
  }

  getVisit(visitId: string): Promise<QueueVisitSnapshot> {
    return this.get<QueueVisitSnapshot>(`/visits/${visitId}`);
  }

  getIntakeForm(): Promise<IntakeFormResponse> {
    return this.get<IntakeFormResponse>('/visits/intake-form');
  }

  recordVisitVitals(visitId: string, body: QueueVitalsBody): Promise<QueueVisitSnapshot> {
    return this.put<QueueVisitSnapshot, QueueVitalsBody>(`/visits/${visitId}/vitals`, body);
  }

  linkPatient(appointmentId: string, patientId: string): Promise<AppointmentResponse> {
    return this.put<AppointmentResponse, { patientId: string }>(`/appointments/${appointmentId}/patient`, { patientId });
  }
}

export const schedulingApi = new SchedulingApiService();
